import React from 'react';

const AssociateEarlyEd = () => {
  const coreCourses = [
    { code: 'ECE 100', name: 'Introduction to ECE', credits: 3 },
    { code: 'ECE 101', name: 'ECE Administration 1', credits: 3 },
    { code: 'ECE 103', name: 'ECE Administration 2', credits: 3 },
    { code: 'ECE 105', name: 'Introduction to Socialization of Children', credits: 3 },
    { code: 'ECE 107', name: 'Curriculum for ECE Programs', credits: 3 },
    { code: 'ECE 109', name: 'Health, Nutrition and First Aid for ECE', credits: 3 }
  ];

  const specialtyElectives = [
    { code: 'ECE 201', name: 'Supporting and Leading Teams in ECE', credits: 3 },
    { code: 'ECE 202', name: 'Child, Family and Community Relations', credits: 3 },
    { code: 'ECE 204', name: 'Supervision for Infant and Toddlers', credits: 3 },
    { code: 'ECE 206', name: 'Children with Exceptionalities', credits: 3 },
    { code: 'ECE 208', name: 'Educational Technology', credits: 3 },
    { code: 'ECE 212', name: 'Language, Literacy Experiences for CEC', credits: 3 },
    { code: 'ECE 301', name: 'Understanding Children Development', credits: 3 },
    { code: 'ECE 302', name: 'Review of ECE and Education Principles and Theories', credits: 3 }
  ];

  const genEdRequirements = [
    { field: 'English Composition', credits: 6 },
    { field: 'Humanities', credits: 6 },
    { field: 'Social Science', credits: 9 },
    { field: 'Natural Science', credits: 3 },
    { field: 'Mathematics', credits: 3 },
    { field: 'One Other General Education Elective', credits: 3 }
  ];

  const features = [
    {
      title: "Comprehensive Foundation",
      description: "Build a strong foundation in early childhood education principles and practices",
      icon: "🎓"
    },
    {
      title: "Practical Skills",
      description: "Develop hands-on skills in classroom management and curriculum development",
      icon: "👥"
    },
    {
      title: "Career Ready",
      description: "Prepare for immediate entry into the early childhood education field",
      icon: "💼"
    },
    {
      title: "Flexible Path",
      description: "Option to continue towards a bachelor's degree in education",
      icon: "🎯"
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-primary-600 to-primary-700">
        <div className="container mx-auto px-4 py-16">
          <div className="max-w-4xl mx-auto text-center text-white">
            <h1 className="text-4xl font-bold mb-4">
              Associate Degree in Early Childhood Education
            </h1>
            <p className="text-xl opacity-90">
              Launch your career in early childhood education with our comprehensive 60-credit program
            </p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-4xl mx-auto">
          {/* Overview Section */}
          <section className="mb-16">
            <div className="bg-white rounded-xl shadow-sm p-8">
              <h2 className="text-2xl font-bold mb-6">Program Overview</h2>
              <p className="text-gray-600 mb-4">
                The Associates of Science Degree in Early Childhood Education (ECE) requires 60 credit hours:
                30 credit hours in ECE education courses and 30 credit hours in general education electives.
              </p>
            </div>
          </section>

          {/* Features Section */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold mb-6">Program Features</h2>
            <div className="grid md:grid-cols-2 gap-6">
              {features.map((feature, index) => (
                <div key={index} className="bg-white rounded-xl shadow-sm p-8 relative overflow-hidden">
                  <div className="absolute top-0 left-0 w-2 h-full bg-primary-500"></div>
                  <div className="flex items-start gap-4">
                    <span className="text-3xl">{feature.icon}</span>
                    <div>
                      <h3 className="text-xl font-semibold mb-2 text-primary-600">
                        {feature.title}
                      </h3>
                      <p className="text-gray-600">{feature.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Core Courses Section */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold mb-6">Core Courses</h2>
            <div className="bg-white rounded-xl shadow-sm overflow-hidden">
              <div className="divide-y divide-gray-200">
                {coreCourses.map((course, index) => (
                  <div key={index} className="p-6">
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">
                          {course.code}: {course.name}
                        </h3>
                      </div>
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-primary-100 text-primary-800">
                        {course.credits} Credits
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Specialty Electives Section */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold mb-6">Specialty Electives (Select Four Courses)</h2>
            <div className="bg-white rounded-xl shadow-sm overflow-hidden">
              <div className="divide-y divide-gray-200">
                {specialtyElectives.map((course, index) => (
                  <div key={index} className="p-6">
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">
                          {course.code}: {course.name}
                        </h3>
                      </div>
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-primary-100 text-primary-800">
                        {course.credits} Credits
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* General Education Requirements Section */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold mb-6">General Education Requirements</h2>
            <div className="bg-white rounded-xl shadow-sm overflow-hidden">
              <div className="p-6">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead>
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Field
                        </th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Credit Hours
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {genEdRequirements.map((req, index) => (
                        <tr key={index}>
                          <td className="px-6 py-4 text-sm text-gray-900">{req.field}</td>
                          <td className="px-6 py-4 text-sm text-gray-900 text-right">{req.credits}</td>
                        </tr>
                      ))}
                      <tr className="bg-gray-50">
                        <td className="px-6 py-4 text-sm font-medium text-gray-900">Total Credit Hours</td>
                        <td className="px-6 py-4 text-sm font-medium text-gray-900 text-right">30</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </section>

          {/* CTA Section */}
          <section>
            <div className="bg-gradient-to-br from-primary-50 to-primary-100 rounded-xl p-8 text-center">
              <h2 className="text-2xl font-bold mb-4">Start Your Educational Journey</h2>
              <p className="text-gray-600 mb-8 max-w-2xl mx-auto">
                Begin your career in early childhood education with our comprehensive associate degree program.
              </p>
              <div className="flex flex-col sm:flex-row justify-center gap-4">
                <a
                  href="/application"
                  className="inline-flex items-center justify-center px-6 py-3 rounded-lg text-white bg-primary-600 hover:bg-primary-700 transition-colors duration-200"
                >
                  Apply Now
                </a>
                <a
                  href="/request"
                  className="inline-flex items-center justify-center px-6 py-3 rounded-lg text-primary-700 bg-white border border-primary-200 hover:bg-primary-50 transition-colors duration-200"
                >
                  Request Information
                </a>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default AssociateEarlyEd; 